// Phase 15 — Draw-to-find recogniser (client-side).
//
// Self-hosted, offline shape matcher. The reference features in
// draw-index.generated.ts were extracted from real glyph outlines at build time
// using the EXACT rasterisation below, so a visitor's pen strokes and the stored
// glyphs end up in the same feature space. Matching is a cosine nearest-neighbour
// — no network, no third-party recogniser.

import { DRAW_GRID, DRAW_INDEX, type DrawRef } from "@/data/draw-index.generated";

const GRID = DRAW_GRID;
const PAD = 0.12;

export interface Point {
  x: number;
  y: number;
}
export type Stroke = Point[];

export interface Match {
  slug: string;
  symbol: string;
  name: string;
  score: number;
}

// --- Feature pipeline (mirror of scripts/build-draw-index.mjs) --------------
function blur3(grid: Float64Array): Float64Array {
  const out = new Float64Array(GRID * GRID);
  const k = [1, 2, 1, 2, 4, 2, 1, 2, 1];
  for (let y = 0; y < GRID; y++) {
    for (let x = 0; x < GRID; x++) {
      let acc = 0;
      let wsum = 0;
      let ki = 0;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const xi = x + dx;
          const yi = y + dy;
          const wgt = k[ki++];
          if (xi < 0 || yi < 0 || xi >= GRID || yi >= GRID) continue;
          acc += grid[yi * GRID + xi] * wgt;
          wsum += wgt;
        }
      }
      out[y * GRID + x] = wsum ? acc / wsum : 0;
    }
  }
  return out;
}

function pointsToGrid(points: Point[]): Float64Array {
  const grid = new Float64Array(GRID * GRID);
  if (points.length === 0) return grid;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  const span = Math.max(maxX - minX || 1, maxY - minY || 1);
  const scale = (GRID * (1 - 2 * PAD)) / span;
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  for (const p of points) {
    const gx = GRID / 2 + (p.x - cx) * scale;
    const gy = GRID / 2 + (p.y - cy) * scale;
    const x0 = Math.floor(gx - 0.5);
    const y0 = Math.floor(gy - 0.5);
    const fx = gx - 0.5 - x0;
    const fy = gy - 0.5 - y0;
    const add = (xi: number, yi: number, wgt: number) => {
      if (xi < 0 || yi < 0 || xi >= GRID || yi >= GRID) return;
      grid[yi * GRID + xi] += wgt;
    };
    add(x0, y0, (1 - fx) * (1 - fy));
    add(x0 + 1, y0, fx * (1 - fy));
    add(x0, y0 + 1, (1 - fx) * fy);
    add(x0 + 1, y0 + 1, fx * fy);
  }
  return blur3(grid);
}

/** Resample a stroke so long pen segments contribute evenly-spaced points. */
function densify(stroke: Stroke): Point[] {
  if (stroke.length < 2) return stroke.slice();
  const out: Point[] = [stroke[0]];
  for (let i = 1; i < stroke.length; i++) {
    const a = stroke[i - 1];
    const b = stroke[i];
    const dist = Math.hypot(b.x - a.x, b.y - a.y);
    const steps = Math.max(1, Math.round(dist / 3));
    for (let s = 1; s <= steps; s++) {
      const t = s / steps;
      out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
    }
  }
  return out;
}

function normalize(grid: Float64Array): Float64Array {
  let n = 0;
  for (const v of grid) n += v * v;
  n = Math.sqrt(n) || 1;
  const out = new Float64Array(grid.length);
  for (let i = 0; i < grid.length; i++) out[i] = grid[i] / n;
  return out;
}

// --- Reference index (decoded once) ----------------------------------------
function decodeB64(b64: string): Uint8Array {
  if (typeof atob === "function") {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  }
  // SSR / Node fallback.
  return Uint8Array.from(Buffer.from(b64, "base64"));
}

interface RefVec {
  ref: DrawRef;
  v: Float64Array;
}
let cachedRefs: RefVec[] | null = null;
function refVectors(): RefVec[] {
  if (cachedRefs) return cachedRefs;
  cachedRefs = DRAW_INDEX.map((ref) => {
    const bytes = decodeB64(ref.v);
    const f = new Float64Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) f[i] = bytes[i];
    return { ref, v: normalize(f) };
  });
  return cachedRefs;
}

export const DRAW_INDEX_SIZE = DRAW_INDEX.length;

/**
 * Rank reference glyphs against the drawn strokes. Returns the top `limit`
 * matches by cosine similarity, or [] when the canvas is effectively empty.
 */
export function recognizeStrokes(strokes: Stroke[], limit = 12): Match[] {
  const points: Point[] = [];
  for (const s of strokes) for (const p of densify(s)) points.push(p);
  if (points.length < 4) return [];

  const q = normalize(pointsToGrid(points));
  const refs = refVectors();
  const scored: Match[] = [];
  for (const { ref, v } of refs) {
    let dot = 0;
    for (let i = 0; i < q.length; i++) dot += q[i] * v[i];
    scored.push({ slug: ref.slug, symbol: ref.symbol, name: ref.name, score: dot });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit);
}
